// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory
import fetch from 'cross-fetch';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import { getMixpanelPlatformProperties } from './mixpanelPlatform';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const baseContentType = h =>
  h
    ? h
        .split(';')[0]
        .trim()
        .toLowerCase()
    : null;

const decodeUtf8 = async res => {
  // Force UTF-8 decoding regardless of header charset
  const buf = await res.arrayBuffer();
  return new TextDecoder('utf-8').decode(buf);
};

const handleRequest = async (url, options) => {
  const res = await fetch(url, options);
  const ct = baseContentType(res.headers.get('Content-Type'));

  if (res.status >= 400) {
    // Try JSON error first; fallback to text (UTF-8)
    try {
      return Promise.reject(await res.json());
    } catch {
      const txt = await decodeUtf8(res);
      return Promise.reject({ error: txt || 'Request failed' });
    }
  }

  // Prefer JSON
  if (ct === 'application/json') {
    // If the server forgot charset, force UTF-8 anyway:
    try {
      return await res.json();
    } catch {
      const txt = await decodeUtf8(res);
      return JSON.parse(txt);
    }
  }

  // Transit fallback
  if (ct === 'application/transit+json') {
    const txt = await decodeUtf8(res);
    try {
      return deserialize(txt); // Transit
    } catch {
      try {
        return JSON.parse(txt); // Mislabelled JSON
      } catch {
        return txt; // Last resort
      }
    }
  }

  // Unknown content-type → try JSON, else return text
  const txt = await decodeUtf8(res);
  try {
    return JSON.parse(txt);
  } catch {
    return txt;
  }
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  // Allow optional AbortSignal via special key `__signal` in body
  let signal;
  let payload = body;
  if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, '__signal')) {
    signal = body.__signal;
    const { __signal, ...rest } = body;
    payload = rest;
  }
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(payload),
    ...(signal ? { signal } : {}),
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        // Preserve the response structure that components expect
        e.response = {
          status: res.status,
          data: data,
        };

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const del = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  let signal;
  let payload = body;
  if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, '__signal')) {
    signal = body.__signal;
    const { __signal, ...rest } = body;
    payload = rest;
  }
  const options = {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(payload),
    ...(signal ? { signal } : {}),
  };
  return handleRequest(url, options);
};

// const get = (path, params = {}, headers) => {
//   const url = `${apiBaseUrl()}${path}`;
//   const queryParams = new URLSearchParams(params).toString();
//   const options = {
//     method: 'GET',
//     credentials: 'include',
//     headers: {
//       'Content-Type': 'application/transit+json',
//       ...headers,
//     },
//   };
//   return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
// };

const get = (path, params = {}, headers) => {
  const url = `${apiBaseUrl()}${path}`;
  const queryParams = new URLSearchParams(params).toString();
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
      ...headers,
    },
  };
  return handleRequest(`${url}${queryParams ? '?' : ''}${queryParams}`, options);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const exchangeMobileWebToken = body => {
  return post('/api/auth/mobile-web-token/exchange', body);
};

export const fetchBlogs = body => {
  return post('/api/fetch-blogs', body);
};
export const fetchTagsFilter = body => {
  return post('/api/fetch-tags-filter', body);
};

export const fetchLandingBlogs = body => {
  const result = post('/api/fetch-landing-blogs', body);
  return result;
};

export const fetchBlog = body => {
  return post('/api/fetch-blog', body);
};

export const fetchTutorials = body => {
  return post('/api/cms/fetch-tutorials', body);
};
export const getTutorialBySlug = body => {
  return post('/api/cms/get-tutorial', body);
};

export const fetchAvailabilties = body => {
  return post('/api/fetch-availabilties', body);
};

export const subscribeMailChimpNewsLetter = body => {
  return post('/api/mailchimp/subscribe', body);
};

export const getGoogleAccessToken = body => {
  return post('/api/google/access-token', body);
};

export const exchangeCode = body => {
  return post('/api/google/exchange-code', body);
};

export const getGoogleCalendarAndEvents = body => {
  return post('/api/google/fetch-calendar-and-events', body);
};

export const createGoogleCalendarEvent = body => {
  return post('/api/google/create-event', body);
};

export const verifyCoupon = body => {
  return post('/api/stripe/verify-coupon', body);
};

export const sendSendGridNotification = body => {
  return post('/api/sendgrid/send-notification', body);
};

export const createSendgridContact = body => {
  return post('/api/sendgrid/create-contact', body);
};

export const updateReceiptParamsInTransaction = body => {
  return post('/api/integration/update-receipt', body);
};

export const updateMeetingLink = body => {
  return post('/api/integration/update-meeting-link', body);
};

export const showStripeAccount = body => {
  return post('/api/show-stripe-account', body);
};

export const deleteCoupon = body => {
  return post('/api/delete-coupon', body);
};

export const uploadObject = body => {
  return post('/api/upload-object', body);
};

//Dashboard API Calls
export const queryUsersData = body => {
  return post('/api/data/query-users', body);
};

export const queryTransactionsData = body => {
  return post('/api/data/query-transactions', body);
};

export const settleProvidePayout = body => {
  return post('/api/settle-provider-payout', body);
};
export const getSubscriptionProductPrice = body => {
  return get('/api/stripe/get-product-price', body);
};

export const getCurrentUserSubscriptionStatus = body => {
  return get('/api/stripe/subscription-status', body);
};

export const createStripeSubscription = body => {
  return post('/api/stripe/create-subscription', body);
};

export const getEvents = body => {
  return post('/api/cms/get-events', body);
};

export const getEventTags = body => {
  return post('/api/cms/get-event-tags', body);
};

export const getEvent = body => {
  return post('/api/cms/get-event', body);
};

export const getTopics = body => {
  return post('/api/cms/get-topics', body);
};

export const getTheBasics = body => {
  return post('/api/cms/get-the-basics', body);
};

export const getTutorialSubcategories = body => {
  return post('/api/cms/get-tutorial-subcategories', body);
};

export const getPopularItems = body => {
  return post('/api/cms/get-popular-items', body);
};

export const getTopicDetails = body => {
  return post('/api/cms/get-topic-details', body);
};

export const getSymptoms = body => {
  return post('/api/cms/get-symptoms', body);
};

export const getSymptomsCategories = body => {
  return post('/api/cms/get-symptoms-categories', body);
};

export const getProducts = body => {
  return post('/api/cms/get-products', body);
};

export const getProductStages = body => {
  return post('/api/cms/get-product-stages', body);
};

export const getProductTypes = body => {
  return post('/api/cms/get-product-types', body);
};

export const verifyVoucherCode = body => {
  return post('/api/verify-voucher-code', body);
};

export const search = body => {
  return post('/api/milvus/search', body);
};

export const getStripeCheckoutSessionPayment = body => {
  const { sessionId } = body;
  return get(`/api/stripe/retrieve-checkout-session`, { sessionId });
};

export const createStripeCheckoutSession = body => {
  return post('/api/stripe/create-checkout-session', body);
};

export const createStripeFreeSubscription = body => {
  return post('/api/stripe/create-free-subscription', body);
};

export const updateUserSubscriptionData = body => {
  return post('/api/stripe/update-user-subscription-data', body);
};

export const confirmStripeCheckoutSession = body => {
  return post('/api/stripe/confirm-stripe-checkout-session', body);
};

export const cancelSubscriptionPlan = body => {
  return post(`/api/stripe/cancel-subscription-plan`, body);
};
export const createStripeSignupSubscription = body => {
  return post(`/api/stripe/create-signup-subscription`, body);
};

export const streamAIChat = body => {
  return post(`/api/gemma/chat-stream`, body);
};

export const getAnswerByQuestion = body => {
  return post(`/api/ai/get-answer-by-question`, body);
};

export const confirmSaveTrackers = body => {
  return post('/api/gemma/trackers/confirm-save', body);
};

export const enrichResources = body => {
  return post(`/api/ai/enrich-resources`, body);
};

export const deleteUserAccount = body => {
  return post('/api/delete-user-account', body);
};

export const listConversations = body => {
  return post('/api/ai/list-conversations', body);
};

export const saveConversation = body => {
  return post('/api/ai/save-conversation', body);
};

export const swapConversation = body => {
  return post('/api/ai/swap-conversation', body);
};

export const deleteConversation = body => {
  return del('/api/ai/delete-conversation', body);
};

export const deleteUserConversationsInBackground = body => {
  return post('/api/ai/delete-user-conversations', body);
};

export const getConsent = body => {
  return post('/api/ai/get-consent', body);
};

export const setConsent = body => {
  return post('/api/ai/set-consent', body);
};

export const searchHistory = body => {
  return post('/api/history/search', body);
};

export const renameConversation = body => {
  return post('/api/history/rename', body);
};

export const getAiDemo = body => {
  return post('/api/ai/get-ai-demo', body);
};

export const submitQuizApi = body => {
  return post('/api/assessment/quiz', body);
};

export const getQuizFieldsApi = body => {
  return post('/api/assessment/quiz/fields', body);
};

export const submitAssessmentCompletedApi = body => {
  return post('/api/assessment/completed', body);
};

export const getAssessmentResultApi = body => {
  return post('/api/assessment/result', body);
};

const MIXPANEL_ALLOWED_HOSTNAMES = new Set([
  'vivianlab.com',
  'www.vivianlab.com',
  'vivianlab.gr',
  'www.vivianlab.gr',
]);

const isMixpanelRequestAllowed = () => {
  if (process.env.REACT_APP_ENV !== 'production' || typeof window === 'undefined') {
    return false;
  }

  const hostname = (window.location?.hostname || '').toLowerCase();
  return MIXPANEL_ALLOWED_HOSTNAMES.has(hostname);
};

const skipMixpanelRequest = reason => Promise.resolve({ skipped: true, reason });

const withMixpanelPlatformProperties = body => ({
  ...(body || {}),
  properties: {
    ...((body && body.properties) || {}),
    ...getMixpanelPlatformProperties(),
  },
});

export const MixPanelApi = {
  trackEvent: body =>
    isMixpanelRequestAllowed()
      ? post('/api/track-event', withMixpanelPlatformProperties(body))
      : skipMixpanelRequest('domain'),
  identifyUser: body =>
    isMixpanelRequestAllowed()
      ? post('/api/identify-user', withMixpanelPlatformProperties(body))
      : skipMixpanelRequest('domain'),
  setUserData: body =>
    isMixpanelRequestAllowed()
      ? post('/api/set-user-data', withMixpanelPlatformProperties(body))
      : skipMixpanelRequest('domain'),
};

export const getTopicDetailById = body => {
  return post('/api/cms/get-topic-detail-by-id', body);
};

export const getSymptomDetailById = body => {
  return post('/api/cms/get-symptom-detail-by-id', body);
};

export const fetchArticlesByTopic = body => {
  return post('/api/cms/get-articles-by-topic', body);
};

// Unified Strapi content search
export const searchContent = body => {
  return post('/api/cms/search', body);
};

export const getRelatedTutorials = body => {
  return post('/api/cms/get-related-tutorials', body);
};

export const getVideoChaptersByIdAPI = body => {
  return post('/api/vimeo/getChapters', body);
};

export const getVideoDurationAPI = body => {
  return post('/api/vimeo/getDuration', body);
};

export const getVideoThumbnailAPI = body => {
  return post('/api/vimeo/getThumbnail', body);
};

export const getVideoThumbnailsAPI = body => {
  return post('/api/vimeo/getThumbnails', body);
};

export const insertSymptoms = body => {
  return post('/api/symptoms/insert-symptoms', body);
};

export const deleteAllSymptoms = body => {
  return post('/api/symptoms/delete-all-symptoms', body);
};

export const getUserSymptoms = body => {
  return post('/api/symptoms/get-symptoms', body);
};

export const getSymptomsBasic = body => {
  return post('/api/symptoms/get-symptoms-basic', body);
};

export const getMoodsBasic = body => {
  return post('/api/moods/get-moods-basic', body);
};

export const insertMoods = body => {
  return post('/api/moods/insert-moods', body);
};

export const selectMood = body => {
  return post('/api/moods/select-mood', body);
};

export const getUserMoods = body => {
  return post('/api/moods/get-moods', body);
};

export const getAllMoodsByUserId = body => {
  return post('/api/moods/get-all-moods-by-user-id', body);
};

export const deleteMood = body => {
  return post('/api/moods/delete-mood', body);
};

export const getMoodAnalytics = body => {
  return post('/api/moods/get-mood-analytics', body);
};

export const getHabitsBasic = body => {
  return post('/api/habits/get-habits-basic', body);
};

export const getHabitInterventionBasic = body => {
  return post('/api/habits/get-habit-intervention-basic', body);
};

export const getHabitInterventionsByUserAndDate = body => {
  return post('/api/habits/get-habit-interventions-by-user-and-date', body);
};

export const upsertHabitIntervention = body => {
  return post('/api/habits/upsert-habit-intervention', body);
};

export const getAllHabitInterventionsByUserId = body => {
  return post('/api/habits/get-all-habit-interventions-by-user-id', body);
};

export const insertHabit = body => {
  return post('/api/habits/insert-habit', body);
};

export const getHabits = body => {
  return post('/api/habits/get-habits', body);
};

export const getAllHabitsByUserId = body => {
  return post('/api/habits/get-all-habits-by-user-id', body);
};

export const getPatternEvents = body => {
  return post('/api/patterns/get-events', body);
};

export const deleteHabit = body => {
  return post('/api/habits/delete-habit', body);
};

export const upsertHistory = body => {
  return post('/api/watch/upsert-history', body);
};

export const getUserHistory = body => {
  return post('/api/watch/get-history', body);
};

export const sendgridUnsubscribe = body => {
  return post('/api/sendgrid/unsubscribe', body);
};

// Previous search results persistence
export const upsertPreviousSearch = body => {
  return post('/api/search/previous/upsert', body);
};
// Previous search results list

export const listPreviousSearchByUser = body => {
  return post('/api/search/previous/list', body);
};

export const getProductBySlug = body => {
  return post('/api/cms/get-product-by-slug', body);
};
export const checkEmailExists = body => {
  return post('/api/check-email-exists', body);
};

export const getWellnessPrograms = body => {
  return post('/api/cms/fetch-wellness-programs', body);
};

export const getWellnessProgramById = body => {
  return post('/api/cms/get-wellness-program-by-id', body);
};
